import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactComponent as MenuIcon } from "../../../assets/icons/menu-icon.svg";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import useNavigationMenuLinks from "../../../hooks/useNavigationMenuLinks";
import { FONT_INTER } from "../../../styles/fonts";
import {
  focusRingInsetSx,
  focusRingOnDarkSx,
  focusRingSx,
} from "../../../styles/focusRing";

const triggerButtonSx = {
  p: "6px",
  borderRadius: "6px",
  color: "#FFFFFF",
  "&:hover": {
    bgcolor: "rgba(255, 255, 255, 0.12)",
  },
  "&.MuiButtonBase-root:focus-visible": focusRingOnDarkSx,
};

const paperSx = {
  width: 280,
  fontFamily: FONT_INTER,
};

const drawerHeaderSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  px: 2,
  py: "14px",
};

const drawerTitleSx = {
  fontFamily: FONT_INTER,
  fontSize: 16,
  fontWeight: 600,
  color: "#1A1A1A",
};

const closeButtonSx = {
  p: "6px",
  "&.MuiButtonBase-root:focus-visible": focusRingSx,
};

const navLinkSx = {
  display: "flex",
  alignItems: "center",
  gap: "12px",
  px: 2,
  py: "10px",
  mx: 1,
  my: "2px",
  borderRadius: "6px",
  textDecoration: "none",
  color: "#3B3C3E",
  "&:hover": {
    bgcolor: "#98D8FE",
  },
  "&:focus-visible": {
    ...focusRingInsetSx,
    bgcolor: "#CBEEFE",
  },
  "&.active": {
    bgcolor: "#98D8FE",
    "& .navLinkLabel": {
      fontWeight: 600,
    },
  },
};

const navLinkLabelSx = {
  fontFamily: FONT_INTER,
  fontSize: 14,
  fontWeight: 500,
  color: "inherit",
};

let menuIdCounter = 0;

export const NavigationMenu = () => {
  const { allowedPages, showNavigationMenu } = useNavigationMenuLinks();
  const [open, setOpen] = useState(false);
  const [menuId] = useState(() => `navigation-menu-${++menuIdCounter}`);
  const titleId = `${menuId}-title`;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  if (!showNavigationMenu || !allowedPages.length) return null;

  return (
    <>
      <Tooltip title="Menu de Navegação">
        <IconButton
          onClick={handleOpen}
          disableRipple
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-controls={open ? menuId : undefined}
          aria-label="Abrir menu de navegação"
          sx={triggerButtonSx}
        >
          <MenuIcon
            width={28}
            height={28}
            aria-hidden="true"
            focusable="false"
          />
        </IconButton>
      </Tooltip>

      <Drawer
        id={menuId}
        anchor="left"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: paperSx,
          role: "dialog",
          "aria-modal": true,
          "aria-labelledby": titleId,
        }}
      >
        <Box sx={drawerHeaderSx}>
          <Typography id={titleId} sx={drawerTitleSx}>
            Menu
          </Typography>
          <IconButton
            onClick={handleClose}
            disableRipple
            autoFocus
            aria-label="Fechar menu de navegação"
            sx={closeButtonSx}
          >
            <CloseRoundedIcon
              sx={{ fontSize: 20 }}
              aria-hidden="true"
              focusable="false"
            />
          </IconButton>
        </Box>

        <Divider />

        <Box component="nav" aria-label="Menu de navegação" sx={{ py: 1 }}>
          {allowedPages.map(({ to, label, icon, showSeparatorAbove }) => (
            <React.Fragment key={to}>
              {showSeparatorAbove && <Divider sx={{ my: 1, mx: 2 }} />}
              <Box
                component={NavLink}
                to={to}
                onClick={handleClose}
                activeClassName="active"
                sx={navLinkSx}
              >
                <Box
                  component="span"
                  aria-hidden="true"
                  sx={{
                    display: "flex",
                    color: "#3B3C3E",
                    "& path[fill]": { fill: "#3B3C3E" },
                    "& [stroke]": { stroke: "#3B3C3E" },
                  }}
                >
                  {icon}
                </Box>
                <Typography className="navLinkLabel" sx={navLinkLabelSx}>
                  {label}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      </Drawer>
    </>
  );
};

export default NavigationMenu;
